/**
 * [perf-probe] Opt-in performance instrumentation for the deck-view lag
 * investigation. Every call site is tagged `[perf-probe]` so the whole batch
 * can be found (and removed) with one grep.
 *
 * Enable, then reload:
 *   localStorage.setItem('flashka:perf', '1')          // web
 *   globalThis.__FLASHKA_PERF = true                    // any runtime (per-session)
 * Disable: localStorage.removeItem('flashka:perf') and reload.
 *
 * When enabled, spans log to the console as `[perf] <label>: <ms>` and are
 * also emitted as User Timing measures, so they show up on the timeline in a
 * DevTools Performance recording. When disabled every helper is a cheap no-op.
 */

let cached: boolean | null = null;

export function perfEnabled(): boolean {
  if (cached !== null) return cached;
  try {
    const g = globalThis as { __FLASHKA_PERF?: boolean; localStorage?: Storage };
    cached = g.__FLASHKA_PERF === true || g.localStorage?.getItem('flashka:perf') === '1';
  } catch {
    cached = false;
  }
  return cached;
}

export function perfNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/**
 * Log a completed span. `extra` is a thunk so building the payload (e.g.
 * `Object.keys(bigMap).length`) costs nothing when the probe is disabled.
 * Spans shorter than `minMs` are dropped to keep high-frequency call sites
 * (per-editor, per-frame) from flooding the console with 0.1ms lines.
 */
export function perfLog(
  label: string,
  startMs: number,
  extra?: () => Record<string, unknown>,
  minMs = 0,
): void {
  if (!perfEnabled()) return;
  const end = perfNow();
  const ms = end - startMs;
  if (ms < minMs) return;
  console.log(`[perf] ${label}: ${ms.toFixed(1)}ms`, extra ? extra() : '');
  try {
    performance.measure(`[perf] ${label}`, { start: startMs, end });
  } catch {
    // User Timing L3 options unsupported — console output is enough.
  }
}

/** One-off event marker (no duration) — e.g. "request-deck-cards sent". */
export function perfMark(label: string, extra?: () => Record<string, unknown>): void {
  if (!perfEnabled()) return;
  console.log(`[perf] ${label}`, extra ? extra() : '');
  try {
    performance.mark(`[perf] ${label}`);
  } catch {
    // ignore
  }
}
